import { useEffect, useState } from 'react';
import { isAppInstalled } from '@/db/services/analytics_service';
import { isStoragePersisted, STORAGE_PERSISTENCE_CHANGED } from '@/lib/storage-persistence';

export interface StorageProtection {
  /** The browser's persistent-storage grant; null while unknown or unsupported. */
  persisted: boolean | null;
  /** Running as the installed app (Home Screen / standalone window). */
  installed: boolean;
  /** The first persisted() check has resolved. */
  checked: boolean;
}

/**
 * The two things that keep the browser from evicting this device's offline
 * data: the app being installed, and the browser's persistent-storage grant
 * (useStorageProtectionBanner decides what counts as protected). Re-checked when
 * a persistence request settles, when the app is installed or switches display
 * mode, and when the tab becomes visible again.
 */
export function useStorageProtection(): StorageProtection {
  const [state, setState] = useState<StorageProtection>(() => ({
    persisted: null,
    installed: isAppInstalled(),
    checked: false,
  }));

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      const persisted = await isStoragePersisted();
      if (cancelled) return;
      setState({ persisted, installed: isAppInstalled(), checked: true });
    };
    const handleVisibility = () => {
      if (!document.hidden) void check();
    };
    const handleCheck = () => void check();

    void check();
    window.addEventListener(STORAGE_PERSISTENCE_CHANGED, handleCheck);
    window.addEventListener('appinstalled', handleCheck);
    document.addEventListener('visibilitychange', handleVisibility);
    const standalone = window.matchMedia?.('(display-mode: standalone)');
    standalone?.addEventListener?.('change', handleCheck);

    return () => {
      cancelled = true;
      window.removeEventListener(STORAGE_PERSISTENCE_CHANGED, handleCheck);
      window.removeEventListener('appinstalled', handleCheck);
      document.removeEventListener('visibilitychange', handleVisibility);
      standalone?.removeEventListener?.('change', handleCheck);
    };
  }, []);

  return state;
}
